import { useEffect, useMemo, useReducer, useState } from "react";
import useLoadValidatedStagedVersions from "../hooks/useLoadValidatedStagedVersion";
import {
  ADD_BLOCKING_RULE,
  BlockingRuleAction,
  blockingRulesReducer,
  CLEAR_BLOCKING_RULES,
  generateRuleKey,
  REMOVE_BLOCKING_RULE,
} from "../reducers/blockingRulesReducer";
import {
  IntegrationComponentNames,
  IntegrationValidatorRuleResult,
  IntegrationValidatorRuleResultType,
  IntegrationValidatorRuleSeverityType,
  PublishIntegrationVersionRequestBodyType,
  ValidatedStagedComponent,
  ValidatorRunStatusType,
} from "../types";
import { PublishModuleContext } from "./PublishModuleContext";
import useLoadMappingTestExecutions from "../hooks/useLoadMappingTestExecutions";
import useInterval from "../../../mapping-tests/utils/useInterval";
import { IntegrationTestSuiteExecutionStatus } from "../../../../models/MappingTests";
import useLoadChecklist from "../hooks/useLoadChecklist";
import { fetchWithAuth } from "../../../../api-client/api_client";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import useLoadCanaryTestInfo from "../hooks/useLoadCanaryTestInfo";
import useLoadCanaryTestResults from "../hooks/useLoadCanaryTestResults";
import useKickOffAutomatedTests from "../automated-tests/hooks/useKickOffAutomatedTests";

const VALIDATOR_RUN_IN_PROGRESS_STATUSES = [
  ValidatorRunStatusType.PENDING,
  ValidatorRunStatusType.IN_PROGRESS,
];

// Context provider component
const PublishModuleContextProvider = ({
  children,
  integrationID,
}: {
  children: JSX.Element;
  integrationID: string;
}) => {
  // PUBLISHING RELATED STATE
  const [isLoadingStagedComponents, setIsLoadingStagedComponents] = useState<boolean>(true);
  const [validatorRunStatus, setValidatorRunStatus] = useState<
    ValidatorRunStatusType | undefined
  >();
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [overrideComment, setOverrideComment] = useState<string | undefined>();
  const [overrideTicket, setOverrideTicket] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [isCanaryTestingToggleEnabled, setIsCanaryTestingToggleEnabled] = useState(true);
  const [isCanaryTestingPublishModalOpen, setIsCanaryTestingPublishModalOpen] = useState(false);
  const [isCanaryTestingAbortModalOpen, setIsCanaryTestingAbortModalOpen] = useState(false);

  const onCanaryTestPublish = () => {
    setIsPublishModalOpen(false);
    setIsSaving(true);
    fetchWithAuth({
      path: `/integrations/versioning/${integrationID}/publish-version-canary-success`,
      method: "POST",
      body: {},
      onResponse: () => {
        showSuccessToast("Successfully published versions!");
        setIsSaving(false);
        refetchPublishModuleInfo();
        refetchCanaryTestInfo();
      },
      onError: () => {
        showErrorToast("Error publishing all versions.");
        setIsSaving(false);
      },
    });
  };

  const onCanaryTestAbort = () => {
    setIsPublishModalOpen(false);
    setIsSaving(true);
    fetchWithAuth({
      path: `/integrations/versioning/${integrationID}/publish-aborted`,
      method: "POST",
      body: {},
      onResponse: () => {
        showSuccessToast("Successfully aborted canary test.");
        setIsSaving(false);
        refetchPublishModuleInfo();
        refetchCanaryTestInfo();
      },
      onError: () => {
        showErrorToast("Error aborting canary test.");
        setIsSaving(false);
      },
    });
  };

  const onCanaryTestRollback = () => {
    setIsPublishModalOpen(false);
    setIsSaving(true);
    fetchWithAuth({
      path: `/integrations/versioning/${integrationID}/publish-rollback`,
      method: "POST",
      body: {},
      onResponse: () => {
        showSuccessToast(
          "Successfully rolled back the publish. All previously staged changes are restaged."
        );
        setIsSaving(false);
        refetchPublishModuleInfo();
        refetchCanaryTestInfo();
      },
      onError: () => {
        showErrorToast("Error rolling back canary test.");
        setIsSaving(false);
      },
    });
  };

  const onPublish = ({
    ticket,
    description,
    override_comment,
    override_ticket,
    blueprint_advanced_configurations = {},
    is_e2e_test_suite_override,
  }: PublishIntegrationVersionRequestBodyType) => {
    setIsPublishModalOpen(false);
    setIsSaving(true);
    fetchWithAuth({
      path: `/integrations/versioning/${integrationID}/publish-version`,
      method: "POST",
      body: {
        ticket,
        description,
        blueprint_advanced_configurations,
        override_comment,
        override_ticket,
        blueprint_validator_results: [],
        enable_canary_testing: isCanaryTestingToggleEnabled,
        is_e2e_test_suite_override: is_e2e_test_suite_override || false,
      },
      onResponse: () => {
        showSuccessToast("Successfully published versions!");
        setIsSaving(false);
        setHasUsedAutomatedTestOverride(false); // Reset override flag after successful publish
        refetchPublishModuleInfo();
        refetchCanaryTestInfo();
      },
      onError: (error) => {
        setIsSaving(false);
        error
          ?.json()
          .then((value: string | Record<string, any>) => {
            let errorMessage = "";
            if (typeof value == "string") {
              errorMessage = value;
            } else {
              errorMessage = value?.non_field_errors ?? JSON.stringify(value);
            }
            showErrorToast(
              `Failed to publish integration version and associated staged components. Error message: \n ${errorMessage}`
            );
            showErrorToast(
              `Please contact the Automation Engineering team by posting in #bpe-and-admin-discussions with details involving your publish request`
            );
          })
          .catch(() => {
            showErrorToast(
              "Failed to publish integration version and associated staged components"
            );
          });
      },
    });
  };

  useEffect(() => {
    if (!isSaving) {
      fetchIntegrationChecklist();
      fetchLatestIntegrationVersionTestSuiteExecution();
    }
  }, [integrationID, isSaving]);

  const {
    canaryTestInfo: canaryTestInfo,
    setCanaryTestInfo,
    fetchCanaryTestInfo,
  } = useLoadCanaryTestInfo({
    integrationID: integrationID,
  });

  // STAGED COMPONENTS - Hook for loading validated staged versions
  const {
    publishModuleInfoV2: publishModuleInfo,
    // setPublishModuleInfoV2: setPublishModuleInfo,
    fetchValidatedStagedVersions,
  } = useLoadValidatedStagedVersions({
    integrationID: integrationID,
    setIsLoading: setIsLoadingStagedComponents,
    setValidatorRunStatus: setValidatorRunStatus,
  });

  const {
    canaryTestResults,
    setCanaryTestResults,
    fetchCanaryTestResultsByIntegration,
  } = useLoadCanaryTestResults();

  // MAPPING TESTS - Hook for loading mapping test executions
  const {
    isFetchingLatestTestSuiteExecution,
    hasFailedFetchingLatestExecution,
    integrationTestSuiteExecution,
    isRunningMappingTests,
    fetchLatestIntegrationVersionTestSuiteExecution,
    onRunTestsForStagedIntegrationVersion,
  } = useLoadMappingTestExecutions({ integrationID: integrationID });

  // INTEGRATION CHECKLIST - Hook for loading integration checklist
  const { integrationChecklist, fetchIntegrationChecklist } = useLoadChecklist({
    integrationID: integrationID,
  });

  // AUTOMATED TESTS - State for automated tests
  const [automatedTestIntegrationVersionId, setAutomatedTestIntegrationVersionId] = useState<
    string | null
  >(null);
  const [isRunningAutomatedTests, setIsRunningAutomatedTests] = useState(false);
  const [triggerAutomatedTestPoll, setTriggerAutomatedTestPoll] = useState<(() => void) | null>(
    null
  );
  const [updateAutomatedTestExecution, setUpdateAutomatedTestExecution] = useState<
    ((execution: any) => void) | null
  >(null);

  // Hook for kicking off automated tests
  const { kickOffTests, isKickingOff } = useKickOffAutomatedTests({
    integrationID,
    integrationVersionId: automatedTestIntegrationVersionId,
    onSuccess: () => {
      // Set running state when tests are kicked off successfully
      setIsRunningAutomatedTests(true);
      // Immediately trigger polling to fetch the test execution status
      if (triggerAutomatedTestPoll) {
        triggerAutomatedTestPoll();
      }
    },
    onError: () => {
      // Reset running state when kick-off fails
      setIsRunningAutomatedTests(false);
    },
  });

  const onRunAutomatedTests = () => {
    kickOffTests();
  };

  // Update isRunningAutomatedTests when kick-off state changes
  useEffect(() => {
    if (isKickingOff) {
      setIsRunningAutomatedTests(true);
    }
  }, [isKickingOff]);

  // Clear publishModuleInfo when integrationID and refetch the validated staged versions changes since this component doesn't unmount when switching integrations and staying on the same publish requests tab
  useEffect(() => {
    refetchPublishModuleInfo();
    refetchCanaryTestInfo();
    refetchCanaryTestResults();
  }, [integrationID]);

  // Fetch the latest test suite execution every 5 seconds if the test suite is not in progress or if fetching the latest execution has failed
  useInterval(
    () => {
      fetchLatestIntegrationVersionTestSuiteExecution();
    },
    (integrationTestSuiteExecution &&
      integrationTestSuiteExecution.status !== IntegrationTestSuiteExecutionStatus.IN_PROGRESS) ||
      hasFailedFetchingLatestExecution
      ? null
      : 5000
  );

  // Fetch the validated staged versions every 2 seconds if the async validator is enabled and the validator run is in progress
  useInterval(
    () => {
      fetchValidatedStagedVersions(true);
    },
    validatorRunStatus && VALIDATOR_RUN_IN_PROGRESS_STATUSES.includes(validatorRunStatus)
      ? 2000
      : null
  );

  // Reducer for managing state of blocking rules that are failing and not overridden
  const [blockingRules, dispatchBlockingRules] = useReducer<
    (state: Set<string>, action: BlockingRuleAction) => Set<string>
  >(blockingRulesReducer, new Set<string>());

  // Helper method to add a blocking rule to the blockingRules state
  const addBlockingRule = (rule: IntegrationValidatorRuleResult) => {
    if (
      rule.result === IntegrationValidatorRuleResultType.SUCCESS ||
      rule.is_overridden ||
      !rule.is_new_failure
    ) {
      return;
    }
    const ruleKey = generateRuleKey(rule);
    dispatchBlockingRules({ type: ADD_BLOCKING_RULE, payload: ruleKey });
  };

  // Helper method to remove a blocking rule from the blockingRules state
  const removeBlockingRule = (rule: IntegrationValidatorRuleResult) => {
    if (rule.result === IntegrationValidatorRuleResultType.SUCCESS) {
      return;
    }
    const ruleKey = generateRuleKey(rule);
    dispatchBlockingRules({ type: REMOVE_BLOCKING_RULE, payload: ruleKey });
  };

  // Adds all the failed blocking rules to the blockingRules state
  useEffect(() => {
    if (publishModuleInfo) {
      Object.values(IntegrationComponentNames).forEach((integrationComponentName) => {
        publishModuleInfo.staged_changes?.[integrationComponentName]?.forEach(
          (component: ValidatedStagedComponent) => {
            component.validator_results.forEach((rule: IntegrationValidatorRuleResult) => {
              if (
                rule.result === IntegrationValidatorRuleResultType.FAILURE &&
                rule.severity === IntegrationValidatorRuleSeverityType.BLOCKING &&
                !rule.is_overridden &&
                rule.is_new_failure
              ) {
                addBlockingRule(rule);
              }
            });
          }
        );
      });
    } else {
      dispatchBlockingRules({ type: CLEAR_BLOCKING_RULES, payload: "" });
    }
  }, [publishModuleInfo]);

  // Check if there are any staged changes by looking at if any of the integration components have staged changes
  const hasChanges = useMemo(() => {
    return !!(
      publishModuleInfo &&
      Object.values(IntegrationComponentNames).some(
        (integrationComponentName) =>
          publishModuleInfo.staged_changes?.[integrationComponentName]?.length > 0
      )
    );
  }, [publishModuleInfo]);

  // Deprecated: Mapping tests are no longer blocking publishes
  const [areMappingTestsFailing, setAreMappingTestsFailing] = useState(false);

  const overrideFailingMappingTest = () => {
    setAreMappingTestsFailing(false);
  };

  // Automated tests blocking state
  const [areAutomatedTestsFailing, setAreAutomatedTestsFailing] = useState(false);
  const [hasUsedAutomatedTestOverride, setHasUsedAutomatedTestOverride] = useState(false);

  const overrideFailingAutomatedTest = () => {
    setAreAutomatedTestsFailing(false);
    setHasUsedAutomatedTestOverride(true);
  };

  const refetchPublishModuleInfo = () => {
    setIsLoadingStagedComponents(true);
    // setPublishModuleInfo(undefined);
    fetchLatestIntegrationVersionTestSuiteExecution();
    fetchValidatedStagedVersions();
  };

  const refetchCanaryTestInfo = () => {
    setCanaryTestInfo(undefined);
    fetchCanaryTestInfo();
  };

  const refetchCanaryTestResults = () => {
    setCanaryTestResults(undefined);
    fetchCanaryTestResultsByIntegration(integrationID);
  };

  // Main check to see if the publish button should be disabled
  // If any of the following are true, the publish button should be disabled:
  //  1. If there are any blocking rules that are failing
  //  2. If there are no changes
  //  3. If automated tests are failing (and not overridden)
  // Note: Mapping tests no longer block publishing (deprecated)
  const isPublishBlocked = useMemo(() => {
    return !!blockingRules.size || !hasChanges || areAutomatedTestsFailing;
  }, [blockingRules, hasChanges, areAutomatedTestsFailing]);

  return (
    <PublishModuleContext.Provider
      value={{
        integrationID,
        isPublishModalOpen,
        setIsPublishModalOpen,
        overrideComment,
        setOverrideComment,
        overrideTicket,
        setOverrideTicket,
        isSaving,
        setIsSaving,
        onPublish,
        isCanaryTestingToggleEnabled,
        setIsCanaryTestingToggleEnabled,
        isCanaryTestingPublishModalOpen,
        setIsCanaryTestingPublishModalOpen,
        onCanaryTestPublish,
        onCanaryTestAbort,
        onCanaryTestRollback,
        isCanaryTestingAbortModalOpen,
        setIsCanaryTestingAbortModalOpen,
        areMappingTestsFailing,
        setAreMappingTestsFailing,
        publishModuleInfo,
        canaryTestInfo,
        canaryTestResults,
        hasChanges,
        isPublishBlocked,
        addBlockingRule,
        removeBlockingRule,
        isFetchingLatestTestSuiteExecution,
        hasFailedFetchingLatestExecution,
        integrationTestSuiteExecution,
        isRunningMappingTests,
        overrideFailingMappingTest,
        fetchLatestIntegrationVersionTestSuiteExecution,
        onRunTestsForStagedIntegrationVersion,
        integrationChecklist,
        fetchIntegrationChecklist,
        refetchPublishModuleInfo,
        isLoadingStagedComponents,
        setIsLoadingStagedComponents,
        validatorRunStatus,
        isRunningAutomatedTests,
        onRunAutomatedTests,
        automatedTestIntegrationVersionId,
        setAutomatedTestIntegrationVersionId,
        setIsRunningAutomatedTests,
        setTriggerAutomatedTestPoll,
        triggerAutomatedTestPoll,
        setUpdateAutomatedTestExecution,
        updateAutomatedTestExecution,
        areAutomatedTestsFailing,
        setAreAutomatedTestsFailing,
        overrideFailingAutomatedTest,
        hasUsedAutomatedTestOverride,
        setHasUsedAutomatedTestOverride,
      }}
    >
      {children}
    </PublishModuleContext.Provider>
  );
};

export default PublishModuleContextProvider;
